import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SmashFoodsBlanketPOLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // SKU / ID of the item
  skuDescription = 'skuDescription', // descriptive name of the item (renamed from 'name')
  details = 'details', // verbose description of the item, optional (renamed from 'description')
  vendorSku = 'vendorSku', // vendor's SKU identifier, optional
  vendorSkuDescription = 'vendorSkuDescription', // vendor's SKU description, optional
  uom = 'uom', // unit of measure
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  freightTerms = 'freightTerms', // shipping terms, optional
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const SmashFoodsBlanketPOaddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
  combineAddressLines: yup.string().nullable().optional(),
  breakCityStateZip: yup.string().nullable().optional(),
})
export type SmashFoodsBlanketPOFileAddressData = yup.InferType<
  typeof SmashFoodsBlanketPOaddressInfoSchema
>

const miscChargeSchema = yup.object().shape({
  label: yup.string().nullable().optional(), // e.g. "Tax", "Shipping", "Freight"
  value: yup.string().nullable().optional(), // dollar amount
  percentage: yup.string().nullable().optional(), // e.g. "5" → renders "Tax (5%)"
})

const lineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

const SmashFoodsBlanketPOitemHeadersSchema = yup.object().shape({
  itemNumber: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const SmashFoodsBlanketPOFileDataSchema = yup.object().shape({
  issuer: SmashFoodsBlanketPOaddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  acceptableDeliveryData: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  vendor: SmashFoodsBlanketPOaddressInfoSchema,
  itemHeaders: SmashFoodsBlanketPOitemHeadersSchema,
  items: yup.array().of(lineItemSchema).required(),
  items2: yup.array().of(lineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  termsAndConditionsUrl: yup.string().nullable().optional(),
  termsAndConditionsLabel: yup.string().nullable().optional(),
  miscCharges: yup.array().of(miscChargeSchema).optional(),
  currency: yup.string().nullable().optional(),
})

export type SmashFoodsBlanketPOFileData = yup.InferType<typeof SmashFoodsBlanketPOFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SmashFoodsBlanketPOFileData>

export const smashFoodsBlanketPOFileMappings: RequiredFileData = {
  issuer: {
    attn: '27d0ca61-3bf9-461a-af50-64102a833b75',
    companyName: '5450eff6-2edb-4880-8965-bfec708a5a25',
    addressLine1: 'd721bbf7-8d4e-473a-9e66-652c0b0a8ab7',
    addressLine2: '76fb1d0d-85cd-4ea0-b6af-214420f5e819',
    city: '3e2336c3-dec9-4845-bb61-7a7e43271b43',
    state: '8f480ecf-488b-44b5-a4d3-3c4b602d6a76',
    zipCode: '7a0ce880-01fb-4912-8c1a-b2ad3fed750d',
    country: '7cba4367-829c-42ad-b737-5cdcc59abf07',
    phoneNumber: '8e90b987-b52c-4a44-8c5b-c9b00a8cc897',
    emailAddress: '87e9130f-2f37-4243-82b2-d2cd4a143908',
    displayMultipleLines: '8de4b1ac-3bee-4317-8263-e61461503f21',
    combineAddressLines: 'fc0a5666-eb7b-4ae3-8203-c7bf582cb63a',
    breakCityStateZip: '863537ab-5420-4b2d-8fc2-915252fb365e',
  },
  orderNumber: 'ec71860a-386b-448c-8cc1-4fdd362b2630',
  paymentTerms: 'b2fb82ee-5008-4ee3-b8e8-31aa8275e3fe',
  dateIssued: '9ac31fe5-c503-44ba-9ecf-4368fb6743e4',
  acceptableDeliveryData: '97dd2e3e-e811-49b0-809d-ccb7af75e357',
  dateReady: '13f2e5f0-b7e4-4235-9d75-e4f2665d4e81',
  vendor: {
    attn: 'f63f1fbd-df5f-4246-bccb-a9665ec78f1a',
    companyName: 'bbe0406d-ed80-4b87-9b72-dda87504d13d',
    addressLine1: '3e5ae031-22c5-4272-84e3-2ee2a8d12f8d',
    addressLine2: 'c77c48a3-ef57-44a3-882d-be6b58b67a6a',
    city: '1cdcfed0-1ec8-4a79-818b-fc180a20748b',
    state: '088a9af9-e1b4-4811-9fe5-41ba8146b8dd',
    zipCode: '5e8ceeff-794d-44c2-abf4-27eb39b30338',
    country: '63941400-461c-4310-949c-4cd935847950',
    phoneNumber: 'd6c5ff9e-dcfc-451b-a777-c563ac9c388d',
    emailAddress: 'c532a8ee-2379-4be9-b393-44956ca7cebe',
    displayMultipleLines: '09cdc892-a0a9-4d0f-be1f-6c7465ba0228',
    combineAddressLines: 'fa332a8a-5097-4267-9f03-f2e31cda5c5d',
    breakCityStateZip: 'c61a228c-f6f7-477e-a53f-9f219131e826',
  },
  itemHeaders: {
    itemNumber: '42f7439e-6e99-4da0-ac01-dab018cb367c',
    itemName: 'ff794a23-0f76-43da-bfd4-219223eb7836',
    skuDescription: '655a7268-5e78-4807-ad06-c47f0234b16f',
    details: '6c941a7b-dde3-43e2-8600-c5cff5627678',
    vendorSku: 'dd5fa752-5219-479f-9fe2-082ae3ac80ae',
    vendorSkuDescription: 'd27f5c3f-48b7-466b-81b0-0bf9b2f5a8be',
    uom: '32855dc7-f48b-4504-822f-1bb153de041e',
    itemQty: '37beb91d-2efc-44c8-8dc4-7a4f6fbf3d24',
    unitPrice: '4aa04466-24f3-4244-9b1b-611a3cedfe8c',
    freightTerms: 'f8f6b5f4-a3ad-41a1-b2c2-6d4b2b5888de',
    lineTotal: '253ff0e2-13c6-4742-aa6c-61cf4e37fa0f',
  },
  items: [
    {
      itemNumber: null,
      itemName: '83d1f291-d6a8-4070-8327-41bb967d1c5e',
      skuDescription: 'a1a7caed-9c2c-443b-8470-02e36a9aec9f',
      details: '0e6376de-be81-4cae-805b-518a48598dc5',
      vendorSku: '7ed71cfe-ca7a-48df-823e-b49463bbf965',
      vendorSkuDescription: '990c4cef-d01b-4e25-9d6b-400638399567',
      uom: 'bb0b877c-8322-4e89-b026-b0c220b3f250',
      itemQty: 'ee5663c9-deab-4612-970d-be4aac71bc6b',
      unitPrice: '692564b2-c9b9-4a8a-ae65-1b8af27af2f6',
      freightTerms: 'c3063b41-dd47-40c0-ae1c-031e7daab7d6',
      lineTotal: '3d4f5111-1697-4073-9433-592d8580c695',
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: '792ae2d0-0c43-4081-8788-20ebfb033351',
      skuDescription: 'b3f9b2c9-4493-45b3-bb84-3a836c0bbdc5',
      details: 'cdde4cdd-08cc-4322-bbc0-266187ddeefe',
      vendorSku: 'eba2ffad-2cbd-49f5-a18a-02124ca52bb8',
      vendorSkuDescription: '0e548090-ef89-4f54-bcb7-8dac6ce22c4e',
      uom: 'b29e0acf-9903-499d-92a4-bd08dab2b33e',
      itemQty: 'b443806e-e86b-46a5-8356-15d75e523184',
      unitPrice: 'da8a6e18-60a6-4b37-9a2e-4e782a46e2b1',
      freightTerms: '6baeb294-c503-4e7b-9d12-818a6b56db0c',
      lineTotal: '4e7b549d-be80-4034-9675-522327ace491',
    },
  ],
  dontRoundUnitPrice: '357a8ea6-2ea9-4416-a7fb-5debe13b4426',
  agreement: '66a1c937-020d-4bab-ac91-b38b87e1667a',
  instructions: '7b004ea9-d069-46d5-aa86-a4848c273bb8',
  notes: '4914fc03-900a-4e55-a710-1c4e173fe1e8',
  useLocaleQuantityFormatting: 'fc3480fa-b172-4662-855e-ac4b03bfa465',
  termsAndConditionsUrl: '2b2ed812-5f87-44a6-9eab-d930688ee639',
  termsAndConditionsLabel: '76e5e03e-56ca-4aaf-acef-9b35fab3b7a3',
  miscCharges: [
    {
      label: '2603f0d6-1803-43a8-881d-d3eab676ef14',
      value: '7545167d-4305-4290-ab0e-1b36bead3ae6',
      percentage: '0c688405-20e7-43ae-8352-a7ccbb56b6a9',
    },
  ],
  currency: '6c230280-6b90-4080-be7d-225f23b85ef1',
}
